"use client";

import { useMemo, useState } from "react";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Heading, Text } from "@/src/components/ui/Typography";

import { UseCaseListItem } from "./UseCaseListItem";
import { UseCaseDetail } from "./UseCaseDetail";
import { UseCaseMobileRow } from "./UseCaseMobileRow";

export interface UseCaseItem {
  title: string;
  situation: string;
  solution: string;
  outcome: string;
}

export interface UseCaseCategory {
  label: string;
  items: UseCaseItem[];
}

export interface UseCasesShowcaseProps {
  title: string;
  intro?: string;
  categories: UseCaseCategory[];
}

export function UseCasesShowcase({ title, intro, categories }: UseCasesShowcaseProps) {
  const flatItems = useMemo(
    () =>
      categories.flatMap((category) =>
        category.items.map((item) => ({ item, categoryLabel: category.label })),
      ),
    [categories],
  );

  const indexByTitle = useMemo(
    () => new Map(flatItems.map((entry, index) => [entry.item.title, index])),
    [flatItems],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const active = flatItems[activeIndex];

  if (flatItems.length === 0 || !active) return null;

  return (
    <Section className="bg-white">
      <Container>
        <Heading as="h2" size="displayLG" className="max-w-175">
          {title}
        </Heading>

        {intro && (
          <Text size="bodyLG" className="mt-4 max-w-175 text-neutral-600">
            {intro}
          </Text>
        )}

        {/* Desktop: list + live detail panel — selecting an item swaps the
            panel in place, so switching never requires scrolling. */}
        <div className="mt-13 hidden gap-12 md:mt-16 md:grid md:grid-cols-2 md:gap-16 lg:gap-24">
          <div className="flex flex-col gap-9">
            {categories.map((category) => (
              <div key={category.label}>
                <Text size="labelSM" className="uppercase tracking-widest text-neutral-500">
                  {category.label}
                </Text>

                <div className="mt-3 flex flex-col">
                  {category.items.map((item) => {
                    const index = indexByTitle.get(item.title) ?? 0;
                    return (
                      <UseCaseListItem
                        key={item.title}
                        number={String(index + 1).padStart(2, "0")}
                        title={item.title}
                        active={index === activeIndex}
                        onSelect={() => setActiveIndex(index)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div key={active.item.title} className="h-fit animate-[fadeIn_0.3s_ease]">
            <UseCaseDetail categoryLabel={active.categoryLabel} item={active.item} />
          </div>
        </div>

        {/* Mobile: inline accordion. */}
        <div className="mt-10 md:hidden">
          {categories.map((category, categoryIndex) => (
            <div key={category.label} className={categoryIndex > 0 ? "mt-8" : undefined}>
              <Text size="labelSM" className="uppercase tracking-widest text-neutral-500">
                {category.label}
              </Text>

              <div className="mt-3">
                {category.items.map((item) => {
                  const index = indexByTitle.get(item.title) ?? 0;
                  return (
                    <UseCaseMobileRow
                      key={item.title}
                      number={String(index + 1).padStart(2, "0")}
                      item={item}
                      active={index === activeIndex}
                      onSelect={() => setActiveIndex(index)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}
